package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ActiveWhiteboard
import fr.manyverse.app.domain.model.DrawPoint
import fr.manyverse.app.domain.model.WhiteboardAction
import fr.manyverse.app.domain.model.WhiteboardColor
import fr.manyverse.app.domain.usecase.ClearWhiteboardUseCase
import fr.manyverse.app.domain.usecase.CloseWhiteboardForAllUseCase
import fr.manyverse.app.domain.usecase.DrawOnWhiteboardUseCase
import fr.manyverse.app.domain.usecase.GetActiveWhiteboardUseCase
import fr.manyverse.app.domain.usecase.GetWhiteboardActionsUseCase
import fr.manyverse.app.domain.usecase.ObserveActiveWhiteboardUseCase
import fr.manyverse.app.domain.usecase.ObserveWhiteboardActionsUseCase
import fr.manyverse.app.domain.usecase.OpenWhiteboardUseCase
import fr.manyverse.app.domain.usecase.ToggleWhiteboardGridUseCase
import fr.manyverse.app.presentation.components.modules.whiteboard.WhiteboardTool
import fr.manyverse.app.presentation.components.modules.whiteboard.strokeColor
import fr.manyverse.app.presentation.components.modules.whiteboard.strokeWidth
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * State representing the current whiteboard state.
 */
data class WhiteboardState(
    val whiteboard: ActiveWhiteboard? = null,
    val strokes: List<WhiteboardAction.Stroke> = emptyList(),
    val showGrid: Boolean = false,
    val isOpen: Boolean = false,
    val isMinimized: Boolean = false
)

/**
 * ViewModel for managing whiteboard state and actions.
 */
class WhiteboardViewModel(
    private val openWhiteboardUseCase: OpenWhiteboardUseCase,
    private val drawOnWhiteboardUseCase: DrawOnWhiteboardUseCase,
    private val clearWhiteboardUseCase: ClearWhiteboardUseCase,
    private val toggleWhiteboardGridUseCase: ToggleWhiteboardGridUseCase,
    private val closeWhiteboardForAllUseCase: CloseWhiteboardForAllUseCase,
    private val observeWhiteboardActionsUseCase: ObserveWhiteboardActionsUseCase,
    private val observeActiveWhiteboardUseCase: ObserveActiveWhiteboardUseCase,
    private val getActiveWhiteboardUseCase: GetActiveWhiteboardUseCase,
    private val getWhiteboardActionsUseCase: GetWhiteboardActionsUseCase,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _launchState = MutableStateFlow<AsyncUiState<ActiveWhiteboard>>(AsyncUiState.Idle)
    val launchState: StateFlow<AsyncUiState<ActiveWhiteboard>> = _launchState.asStateFlow()

    private val _whiteboardState = MutableStateFlow(WhiteboardState())
    val whiteboardState: StateFlow<WhiteboardState> = _whiteboardState.asStateFlow()

    private val _selectedColor = MutableStateFlow(WhiteboardColor.BLACK)
    val selectedColor: StateFlow<WhiteboardColor> = _selectedColor.asStateFlow()

    private val _strokeWidth = MutableStateFlow(4f)
    val strokeWidth: StateFlow<Float> = _strokeWidth.asStateFlow()

    private val _selectedTool = MutableStateFlow(WhiteboardTool.PEN)
    val selectedTool: StateFlow<WhiteboardTool> = _selectedTool.asStateFlow()

    private var observeActionsJob: Job? = null
    private var observeActiveWhiteboardsJob: Job? = null
    private var observingTableId: String? = null
    private var observingUserId: String? = null
    private var observingWhiteboardId: String? = null

    /**
     * Opens a whiteboard (GM action).
     * Uses get-or-create pattern: reuses existing whiteboard or creates new one.
     * Resets content (clean slate), starts observing actions, sets isOpen=true.
     */
    fun openWhiteboard(
        tableId: String,
        createdBy: String,
        targetParticipants: List<String>,
        showGrid: Boolean = false
    ) {
        launchAsyncWithCallback(
            state = _launchState,
            block = {
                openWhiteboardUseCase(
                    tableId = tableId,
                    createdBy = createdBy,
                    targetParticipants = targetParticipants,
                    showGrid = showGrid
                )
            },
            onSuccess = { whiteboard ->
                _whiteboardState.value = WhiteboardState(
                    whiteboard = whiteboard,
                    showGrid = showGrid,
                    isOpen = true,
                    isMinimized = false
                )
                startObservingActions(tableId, whiteboard.id)
            }
        )
    }

    /**
     * Minimizes the whiteboard overlay to a floating bubble.
     * Observation of actions continues in the background.
     */
    fun minimize() {
        _whiteboardState.value = _whiteboardState.value.copy(
            isOpen = false,
            isMinimized = true
        )
    }

    /**
     * Restores the whiteboard from the minimized bubble.
     */
    fun restore() {
        _whiteboardState.value = _whiteboardState.value.copy(
            isOpen = true,
            isMinimized = false
        )
    }

    /**
     * Starts observing active whiteboards for a player.
     * When a whiteboard appears targeting this user, resets content and opens overlay.
     * When whiteboard is closed (null), resets the entire state.
     */
    fun startObservingActiveWhiteboards(
        tableId: String,
        currentUserId: String
    ) {
        if (observeActiveWhiteboardsJob?.isActive == true && observingTableId == tableId && observingUserId == currentUserId) {
            return
        }
        observeActiveWhiteboardsJob?.cancel()
        observingTableId = tableId
        observingUserId = currentUserId
        observeActiveWhiteboardsJob = viewModelScope.launch {
            // Snapshot at join time: show pre-existing whiteboard as a bubble
            getActiveWhiteboardUseCase(tableId).onSuccess { existing ->
                if (existing != null && currentUserId in existing.targetParticipants) {
                    _whiteboardState.value = WhiteboardState(
                        whiteboard = existing,
                        showGrid = existing.showGrid,
                        isOpen = false,
                        isMinimized = true
                    )
                    startObservingActions(tableId, existing.id)
                }
            }
            observeActiveWhiteboardUseCase(
                tableId = tableId,
                currentUserId = currentUserId
            ).collect { whiteboard ->
                if (whiteboard != null) {
                    val isNewWhiteboard = _whiteboardState.value.whiteboard?.id != whiteboard.id
                    if (isNewWhiteboard) {
                        // New whiteboard from GM mid-session → open overlay
                        _whiteboardState.value = WhiteboardState(
                            whiteboard = whiteboard,
                            showGrid = whiteboard.showGrid,
                            isOpen = true,
                            isMinimized = false
                        )
                    } else {
                        // Same whiteboard updated (e.g. grid toggle): preserve open/minimized state
                        _whiteboardState.value = _whiteboardState.value.copy(
                            whiteboard = whiteboard,
                            showGrid = whiteboard.showGrid
                        )
                    }
                    startObservingActions(tableId, whiteboard.id)
                } else {
                    // Whiteboard closed: reset everything
                    resetFullState()
                }
            }
        }
    }

    /**
     * Re-synchronizes whiteboard state at foreground return.
     * Restarts action observation if a whiteboard is already open (handles GM reconnect),
     * and checks for a new active whiteboard targeting the user (handles player reconnect).
     */
    fun refreshOnForeground(tableId: String, currentUserId: String) {
        viewModelScope.launch {
            val currentWhiteboard = _whiteboardState.value.whiteboard
            if (currentWhiteboard != null) {
                startObservingActions(tableId, currentWhiteboard.id)
            }
            getActiveWhiteboardUseCase(tableId).onSuccess { whiteboard ->
                val isTargetedAtUser = whiteboard != null && currentUserId in whiteboard.targetParticipants
                val isCreator = whiteboard?.createdBy == currentUserId
                if (whiteboard == null || (!isTargetedAtUser && !isCreator)) {
                    // Fermé ou retiré à l'utilisateur pendant une coupure : la fermeture n'a pas été
                    // reçue. Un tableau ouvert entre-temps par le MJ n'est pas concerné
                    if (currentWhiteboard != null && _whiteboardState.value.whiteboard?.id == currentWhiteboard.id) {
                        resetFullState()
                    }
                    return@onSuccess
                }
                if (_whiteboardState.value.whiteboard?.id == whiteboard.id) return@onSuccess
                // Player receives new whiteboard from GM (background) → open overlay
                // GM reconnects to own whiteboard on fresh session → show bubble
                _whiteboardState.value = WhiteboardState(
                    whiteboard = whiteboard,
                    showGrid = whiteboard.showGrid,
                    isOpen = isTargetedAtUser,
                    isMinimized = !isTargetedAtUser
                )
                startObservingActions(tableId, whiteboard.id)
            }
        }
    }

    /**
     * Draws a stroke on the whiteboard with the active tool.
     * The eraser is a thick stroke in the whiteboard background color.
     */
    fun drawStroke(
        tableId: String,
        points: List<DrawPoint>
    ) {
        val whiteboard = _whiteboardState.value.whiteboard ?: return
        val tool = _selectedTool.value
        val currentColor = tool.strokeColor(_selectedColor.value)
        val currentStrokeWidth = tool.strokeWidth(_strokeWidth.value)

        // Add stroke locally immediately for responsiveness
        val newStroke = WhiteboardAction.Stroke(
            points = points,
            color = currentColor,
            strokeWidth = currentStrokeWidth
        )
        _whiteboardState.value = _whiteboardState.value.copy(
            strokes = _whiteboardState.value.strokes + newStroke
        )

        // Broadcast to other participants
        viewModelScope.launch {
            drawOnWhiteboardUseCase(
                tableId = tableId,
                whiteboardId = whiteboard.id,
                points = points,
                color = currentColor,
                strokeWidth = currentStrokeWidth,
                performedBy = whiteboard.createdBy
            ).onFailure { error ->
                // Les joueurs ne l'ont pas reçu : on ne le laisse pas croire au MJ
                _whiteboardState.update { it.copy(strokes = it.strokes - newStroke) }
                errorNotifier.notify(error)
            }
        }
    }

    /**
     * Clears all content from the whiteboard.
     */
    fun clearAll(
        tableId: String
    ) {
        val whiteboard = _whiteboardState.value.whiteboard ?: return
        val clearedStrokes = _whiteboardState.value.strokes

        // Clear locally immediately
        _whiteboardState.value = _whiteboardState.value.copy(
            strokes = emptyList(),
        )

        // Broadcast to other participants
        viewModelScope.launch {
            clearWhiteboardUseCase(
                tableId = tableId,
                whiteboardId = whiteboard.id,
                performedBy = whiteboard.createdBy
            ).onFailure { error ->
                // Restaure les traits effacés, devant ceux tracés depuis
                _whiteboardState.update { it.copy(strokes = clearedStrokes + it.strokes) }
                errorNotifier.notify(error)
            }
        }
    }

    /**
     * Toggles the grid visibility.
     */
    fun toggleGrid(
        tableId: String
    ) {
        val whiteboard = _whiteboardState.value.whiteboard ?: return
        val newShowGrid = !_whiteboardState.value.showGrid

        // Update locally immediately
        _whiteboardState.value = _whiteboardState.value.copy(showGrid = newShowGrid)

        // Broadcast to other participants
        viewModelScope.launch {
            toggleWhiteboardGridUseCase(
                tableId = tableId,
                whiteboardId = whiteboard.id,
                showGrid = newShowGrid,
                performedBy = whiteboard.createdBy
            ).onFailure { error ->
                _whiteboardState.update { it.copy(showGrid = !newShowGrid) }
                errorNotifier.notify(error)
            }
        }
    }

    /**
     * Closes the whiteboard for all participants (GM only).
     */
    fun closeForAll(
        tableId: String
    ) {
        val whiteboard = _whiteboardState.value.whiteboard ?: return

        viewModelScope.launch {
            closeWhiteboardForAllUseCase(
                tableId = tableId,
                whiteboardId = whiteboard.id,
                performedBy = whiteboard.createdBy
            ).onSuccess {
                resetFullState()
            }.onFailure(errorNotifier::notify)
        }
    }

    /**
     * Sets the drawing color.
     */
    fun setColor(color: WhiteboardColor) {
        _selectedColor.value = color
    }

    /**
     * Sets the active drawing tool (pen or eraser).
     */
    fun setTool(tool: WhiteboardTool) {
        _selectedTool.value = tool
    }

    /**
     * Sets the stroke width.
     */
    fun setStrokeWidth(width: Float) {
        _strokeWidth.value = width.coerceIn(1f, 20f)
    }

    /**
     * Resets the launch state.
     */
    fun resetLaunchState() {
        _launchState.value = AsyncUiState.Idle
    }

    private fun resetFullState() {
        _whiteboardState.value = WhiteboardState()
        stopObservingActions()
    }

    private fun startObservingActions(
        tableId: String,
        whiteboardId: String
    ) {
        if (observeActionsJob?.isActive == true && observingWhiteboardId == whiteboardId) {
            return
        }
        observeActionsJob?.cancel()
        observingWhiteboardId = whiteboardId
        observeActionsJob = viewModelScope.launch {
            getWhiteboardActionsUseCase(whiteboardId).onSuccess { actions ->
                applyHistoricalActions(actions)
            }
            observeWhiteboardActionsUseCase(
                tableId = tableId,
                whiteboardId = whiteboardId
            ).collect { action ->
                handleReceivedAction(action)
            }
        }
    }

    private fun applyHistoricalActions(actions: List<WhiteboardAction>) {
        var strokes = emptyList<WhiteboardAction.Stroke>()
        for (action in actions) {
            when (action) {
                is WhiteboardAction.Stroke -> strokes = strokes + action
                is WhiteboardAction.Clear -> strokes = emptyList()
                is WhiteboardAction.CloseForAll -> strokes = emptyList()
                is WhiteboardAction.ToggleGrid -> Unit  // showGrid comes from whiteboard metadata
            }
        }
        _whiteboardState.value = _whiteboardState.value.copy(strokes = strokes)
    }

    private fun stopObservingActions() {
        observeActionsJob?.cancel()
        observeActionsJob = null
        observingWhiteboardId = null
    }

    private fun handleReceivedAction(action: WhiteboardAction) {
        when (action) {
            is WhiteboardAction.Stroke -> {
                _whiteboardState.value = _whiteboardState.value.copy(
                    strokes = _whiteboardState.value.strokes + action
                )
            }

            is WhiteboardAction.Clear -> {
                _whiteboardState.value = _whiteboardState.value.copy(
                    strokes = emptyList()
                )
            }

            is WhiteboardAction.ToggleGrid -> {
                _whiteboardState.value = _whiteboardState.value.copy(
                    showGrid = action.showGrid
                )
            }

            is WhiteboardAction.CloseForAll -> {
                resetFullState()
            }
        }
    }

    override fun onCleared() {
        super.onCleared()
        stopObservingActions()
        observeActiveWhiteboardsJob?.cancel()
    }
}
